import * as DOMPurify from 'dompurify';

/**
 * Apoio à exibição de notificações: aparência por tipo, resumo em texto puro e
 * montagem do corpo em HTML com links tratados.
 *
 * Espelha o NotificacaoLinks do desktop (jkaw): URL crua no texto vira botão com
 * rótulo curto, link comum abre sempre em outra aba, e quem decide se o corpo é
 * HTML é o conteúdo — tipoNotificacaoTexto chega TXT (padrão da coluna) mesmo
 * quando o texto é HTML.
 */

export interface NotificacaoVisual {
  rotulo: string;
  icone: string;
  cor: string;
  corSuave: string;
}

const VISUAIS: { [tipo: string]: NotificacaoVisual } = {
  AVISO:       { rotulo: 'Aviso',       icone: 'info',          cor: '#f59e0b', corSuave: '#fff5e0' },
  COBRANCA:    { rotulo: 'Cobrança',    icone: 'attach_money',  cor: '#e53935', corSuave: '#fdecea' },
  VERSAO:      { rotulo: 'Versão',      icone: 'system_update', cor: '#1e88e5', corSuave: '#e8f2fd' },
  PROMOCAO:    { rotulo: 'Promoção',    icone: 'local_offer',   cor: '#43a047', corSuave: '#e9f6ea' },
  COMPROMISSO: { rotulo: 'Compromisso', icone: 'event',         cor: '#8e24aa', corSuave: '#f5e9f8' },
  OUTRO:       { rotulo: 'Outro',       icone: 'notifications', cor: '#78909c', corSuave: '#eceff1' },
};

/** Tamanho máximo do rótulo visível de um link. */
const LIMITE_ROTULO = 46;

/** URL solta no meio do texto (inclusive sem protocolo, no caso de www.). */
const URL_SOLTA = /(https?:\/\/[^\s<>"']+|www\.[^\s<>"']+)/gi;

/** Tags que caracterizam um corpo em HTML (e não um "<" perdido no texto). */
const TAG_HTML = /<\s*\/?\s*(p|br|div|span|a|strong|b|i|em|u|ul|ol|li|table|tr|td|th|thead|tbody|h[1-6]|img|hr|blockquote|pre|code|font|center|small|section)\b[^>]*>/i;

const TAGS_PERMITIDAS = ['b', 'i', 'em', 'strong', 'u', 'p', 'br', 'ul', 'ol', 'li', 'a', 'span', 'div', 'img',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'blockquote', 'pre', 'code', 'small', 'font', 'center',
  'table', 'thead', 'tbody', 'tr', 'td', 'th', 'section'];

const ATRIBUTOS_PERMITIDOS = ['href', 'src', 'alt', 'title', 'target', 'rel', 'class', 'style',
  'cellpadding', 'cellspacing', 'border', 'colspan', 'rowspan', 'width', 'height', 'align', 'color'];

export function notificacaoVisual(tipo: any): NotificacaoVisual {
  return VISUAIS[String(tipo ?? '').toUpperCase()] ?? VISUAIS.AVISO;
}

/** Corpo com marcação HTML de verdade. */
export function pareceHtml(texto?: string): boolean {
  return !!texto && TAG_HTML.test(texto);
}

/** Texto para resumo: sem tags, sem entidades HTML e sem espaços repetidos. */
export function textoSimples(texto?: string): string {
  if (!texto) {
    return '';
  }
  const semBlocos = texto.replace(/<\s*(script|style)[^>]*>[\s\S]*?<\s*\/\s*(script|style)\s*>/gi, ' ');
  const doc = new DOMParser().parseFromString(semBlocos.replace(/<[^>]*>/g, ' '), 'text/html');
  return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
}

/** Rótulo curto e legível para uma URL (sem protocolo, com reticências no fim). */
export function rotuloUrl(url: string): string {
  const limpo = (url || '').trim().replace(/^https?:\/\//i, '').replace(/\/$/, '');
  return limpo.length > LIMITE_ROTULO ? limpo.substring(0, LIMITE_ROTULO - 1) + '…' : limpo;
}

/** Notificação pronta para exibir: corpo tratado, capa e botão de ação. */
export interface NotificacaoMontada {
  corpoHtml: string;
  capaUrl: string;
  capaLink: string;
  ctaUrl: string;
  ctaTexto: string;
  /** Dados técnicos (certificado) vindos de outrosDados, quando houver. */
  detalhes: any;
}

/**
 * Chaves opcionais em outrosDados (JSON) para campanhas:
 * {"imagem": "https://.../capa.jpg", "ctaTexto": "Ler no blog", "ctaUrl": "https://infokaw.com.br/blog/..."}.
 * Sem elas, a capa é a imagem que abre o corpo e o botão aponta para o primeiro link.
 */
export function montarNotificacao(n: { id?: any; texto?: string; tipoNotificacao?: any; outrosDados?: string }): NotificacaoMontada {
  const extras = lerOutrosDados(n?.outrosDados);
  const campanha = `notificacao-${n?.id ?? ''}`;

  const raiz = document.createElement('div');
  raiz.innerHTML = sanitizar(n?.texto);
  transformarUrlsSoltas(raiz);
  tratarLinks(raiz, campanha);

  let capaUrl = textoOu(extras.imagem ?? extras.image);
  let capaLink = '';
  if (!capaUrl) {
    const capa = extrairCapa(raiz);
    capaUrl = capa.src;
    capaLink = capa.link;
  }

  let ctaUrl = textoOu(extras.ctaUrl);
  if (ctaUrl) {
    ctaUrl = comUtm(ctaUrl, campanha);
  } else {
    const primeiro = Array.from(raiz.querySelectorAll('a'))
      .map(a => a.getAttribute('href') || '')
      .find(h => /^https?:\/\//i.test(h));
    ctaUrl = primeiro || capaLink;
  }

  const temCertificado = extras && (extras.certificado_vencimento || extras.dias_para_vencer || extras.status);
  return {
    corpoHtml: raiz.innerHTML,
    capaUrl,
    capaLink: capaLink || ctaUrl,
    ctaUrl,
    ctaTexto: textoOu(extras.ctaTexto) || textoCta(ctaUrl, n?.tipoNotificacao),
    detalhes: temCertificado ? extras : null,
  };
}

/** Compatível com o uso antigo: só o corpo tratado. */
export function corpoNotificacaoHtml(texto?: string): string {
  return montarNotificacao({ texto }).corpoHtml;
}

/**
 * Link para o site da Infokaw ganha UTM (sem sobrescrever o que já vier), para o
 * clique na notificação aparecer separado no Analytics do blog e nas conversões.
 */
export function comUtm(url: string, campanha: string): string {
  try {
    const u = new URL(url);
    if (!/(^|\.)infokaw\.com\.br$/i.test(u.hostname) || u.searchParams.has('utm_source')) {
      return url;
    }
    // Rotas do SPA (/#/...) não levam UTM: o hash é da aplicação, não do site.
    if (u.hash.startsWith('#/')) {
      return url;
    }
    u.searchParams.set('utm_source', 'jkaw-web');
    u.searchParams.set('utm_medium', 'notificacao');
    if (campanha) {
      u.searchParams.set('utm_campaign', campanha);
    }
    return u.toString();
  } catch {
    return url;
  }
}

function sanitizar(texto?: string): string {
  if (!texto) {
    return '';
  }
  const html = pareceHtml(texto)
    ? texto
    : escapar(texto).replace(/\r?\n/g, '<br>');
  const purifier: any = (DOMPurify as any).default ? (DOMPurify as any).default : DOMPurify;
  return purifier.sanitize(html, {
    ALLOWED_TAGS: TAGS_PERMITIDAS,
    ALLOWED_ATTR: ATRIBUTOS_PERMITIDOS,
  });
}

/**
 * Imagem que abre o corpo (antes de qualquer texto) sai do corpo e vira capa do
 * card — é o formato natural de um post de blog ou de uma campanha.
 */
function extrairCapa(raiz: HTMLElement): { src: string; link: string } {
  const img = raiz.querySelector('img');
  if (!img) {
    return { src: '', link: '' };
  }
  const antes = document.createRange();
  antes.setStart(raiz, 0);
  antes.setEndBefore(img);
  if ((antes.toString() || '').trim()) {
    return { src: '', link: '' };
  }
  const src = img.getAttribute('src') || '';
  const link = img.closest('a')?.getAttribute('href') || '';
  // remove a imagem e os invólucros que ficariam vazios
  let alvo: Element = img;
  while (alvo.parentElement && alvo.parentElement !== raiz && !(alvo.parentElement.textContent || '').trim()
         && alvo.parentElement.querySelectorAll('img').length === 1) {
    alvo = alvo.parentElement;
  }
  alvo.remove();
  // <br> que sobrou no começo
  while (raiz.firstChild && (raiz.firstChild.nodeName === 'BR'
         || (raiz.firstChild.nodeType === Node.TEXT_NODE && !(raiz.firstChild.nodeValue || '').trim()))) {
    raiz.removeChild(raiz.firstChild);
  }
  return { src, link };
}

function textoCta(url: string, tipo: any): string {
  if (!url) {
    return '';
  }
  if (/\/blog\//i.test(url)) {
    return 'Ler o artigo';
  }
  if (/wa\.me|whatsapp/i.test(url)) {
    return 'Falar no WhatsApp';
  }
  switch (String(tipo ?? '').toUpperCase()) {
    case 'COBRANCA': return 'Abrir cobrança';
    case 'VERSAO':   return 'Ver novidades';
    case 'PROMOCAO': return 'Quero aproveitar';
    default:         return 'Saiba mais';
  }
}

function lerOutrosDados(outrosDados?: string): any {
  if (!outrosDados) {
    return {};
  }
  try {
    const obj = JSON.parse(outrosDados);
    return obj && typeof obj === 'object' ? obj : {};
  } catch {
    return {};
  }
}

function textoOu(valor: any): string {
  return typeof valor === 'string' ? valor.trim() : '';
}

/** URL em nó de texto (fora de <a>) vira botão. */
function transformarUrlsSoltas(raiz: HTMLElement) {
  const walker = document.createTreeWalker(raiz, NodeFilter.SHOW_TEXT);
  const alvos: Text[] = [];
  let no: Node | null;
  while ((no = walker.nextNode())) {
    if (no.parentElement?.closest('a')) {
      continue;
    }
    URL_SOLTA.lastIndex = 0;
    if (URL_SOLTA.test(no.nodeValue || '')) {
      alvos.push(no as Text);
    }
  }
  for (const alvo of alvos) {
    const frag = document.createDocumentFragment();
    const valor = alvo.nodeValue || '';
    let ultimo = 0;
    URL_SOLTA.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = URL_SOLTA.exec(valor))) {
      // pontuação final costuma ser da frase, não da URL
      const bruto = m[0];
      const url = bruto.replace(/[.,;:)\]]+$/, '');
      frag.appendChild(document.createTextNode(valor.substring(ultimo, m.index)));
      const a = document.createElement('a');
      a.href = /^www\./i.test(url) ? 'https://' + url : url;
      a.textContent = url;
      frag.appendChild(a);
      ultimo = m.index + url.length;
    }
    frag.appendChild(document.createTextNode(valor.substring(ultimo)));
    alvo.parentNode?.replaceChild(frag, alvo);
  }
}

/** Todo link abre em outra aba; link cujo texto é a própria URL vira botão curto. */
function tratarLinks(raiz: HTMLElement, campanha: string) {
  raiz.querySelectorAll('a').forEach(a => {
    const original = (a.getAttribute('href') || '').trim();
    if (!original || original.startsWith('#')) {
      return;
    }
    const href = comUtm(original, campanha);
    a.setAttribute('href', href);
    a.setAttribute('target', '_blank');
    a.setAttribute('rel', 'noopener noreferrer');
    if (!a.title) {
      a.title = original;
    }
    const texto = (a.textContent || '').trim();
    const ehUrl = texto === original || /^(https?:\/\/|www\.)/i.test(texto);
    if (ehUrl && !a.querySelector('img')) {
      a.classList.add('nt-chip');
      a.textContent = rotuloUrl(texto);
    }
  });
}

function escapar(texto: string): string {
  return texto
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
